import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../styles/admin/AdminJobs.css';

const AdminJobs = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    filled: 0,
    views: 0
  });
  
  // Filters and pagination
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    department: '',
    job_type: '',
    location: ''
  });
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  // Selection and bulk actions
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [bulkAction, setBulkAction] = useState('');

  const API_BASE_URL = "https://phucansolutions.vn" || '"https://phucansolutions.vn"';

  const departments = [
    { value: 'engineering', label: 'Kỹ thuật' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'sales', label: 'Kinh doanh' },
    { value: 'hr', label: 'Nhân sự' },
    { value: 'finance', label: 'Tài chính' },
    { value: 'operations', label: 'Vận hành' },
    { value: 'design', label: 'Thiết kế' },
    { value: 'product', label: 'Sản phẩm' }
  ];

  const jobTypes = [
    { value: 'full-time', label: 'Toàn thời gian' },
    { value: 'part-time', label: 'Bán thời gian' },
    { value: 'contract', label: 'Hợp đồng' },
    { value: 'internship', label: 'Thực tập' },
    { value: 'freelance', label: 'Freelance' }
  ];

  const statuses = [
    { value: 'active', label: 'Đang tuyển' },
    { value: 'paused', label: 'Tạm dừng' },
    { value: 'filled', label: 'Đã tuyển đủ' },
    { value: 'expired', label: 'Hết hạn' }
  ];

  useEffect(() => {
    fetchJobs();
    fetchStats();
  }, [pagination.page, pagination.limit, filters]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/jobs/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      const queryParams = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      });

      const response = await fetch(`${API_BASE_URL}/api/admin/jobs?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setJobs(data.data.jobs || []);
        setPagination(prev => ({
          ...prev,
          total: data.data.pagination?.total || 0,
          totalPages: data.data.pagination?.totalPages || 0
        }));
      } else {
        console.error('Failed to fetch jobs:', data.error);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPagination(prev => ({
      ...prev,
      page: 1
    }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const handleStatusToggle = async (jobId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'paused' : 'active';
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${API_BASE_URL}/api/admin/jobs/${jobId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      const data = await response.json();
      if (data.success) {
        fetchJobs();
        fetchStats();
      } else {
        alert('Lỗi: ' + data.error);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Lỗi kết nối server');
    }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa tin tuyển dụng này?')) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/jobs/${jobId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        fetchJobs();
        fetchStats();
        alert('Xóa thành công!');
      } else {
        alert('Lỗi: ' + data.error);
      }
    } catch (error) {
      console.error('Error deleting job:', error);
      alert('Lỗi kết nối server');
    }
  };

  const handleSelectJob = (jobId, checked) => {
    setSelectedJobs(prev => 
      checked 
        ? [...prev, jobId]
        : prev.filter(id => id !== jobId)
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedJobs(jobs.map(job => job.id));
    } else {
      setSelectedJobs([]);
    }
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedJobs.length === 0) {
      alert('Vui lòng chọn hành động và ít nhất một tin tuyển dụng');
      return;
    }

    if (!window.confirm(`Bạn có chắc muốn thực hiện hành động "${bulkAction}" cho ${selectedJobs.length} tin tuyển dụng?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const endpoint = bulkAction === 'delete' ? 'bulk-delete' : 'bulk-update';
      const payload = bulkAction === 'delete' 
        ? { ids: selectedJobs }
        : { ids: selectedJobs, updates: { status: bulkAction } };

      const response = await fetch(`${API_BASE_URL}/api/admin/jobs/${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        fetchJobs();
        fetchStats();
        setSelectedJobs([]);
        setBulkAction('');
        alert('Thực hiện thành công!');
      } else {
        alert('Lỗi: ' + data.error);
      }
    } catch (error) {
      console.error('Error performing bulk action:', error);
      alert('Lỗi kết nối server');
    }
  };

  const formatSalary = (minSalary, maxSalary) => {
    if (!minSalary && !maxSalary) return 'Thỏa thuận';
    if (!maxSalary) return `Từ ${minSalary.toLocaleString()} VND`;
    if (!minSalary) return `Lên đến ${maxSalary.toLocaleString()} VND`;
    return `${minSalary.toLocaleString()} - ${maxSalary.toLocaleString()} VND`;
  };

  const getStatusBadge = (status) => {
    const badges = {
      'active': { class: 'status-success', label: 'Đang tuyển' },
      'paused': { class: 'status-warning', label: 'Tạm dừng' },
      'filled': { class: 'status-info', label: 'Đã tuyển đủ' },
      'expired': { class: 'status-danger', label: 'Hết hạn' }
    };
    return badges[status] || { class: 'status-secondary', label: status };
  };

  const getDepartmentLabel = (department) => {
    const dept = departments.find(d => d.value === department);
    return dept ? dept.label : department;
  };

  const getJobTypeLabel = (jobType) => {
    const type = jobTypes.find(t => t.value === jobType);
    return type ? type.label : jobType;
  };

  const handleToggleStatus = (job) => {
    handleStatusToggle(job.id, job.status);
  };

  const handleDeleteJob = (job) => {
    handleDelete(job.id);
  };

  return (
    <div className="admin-jobs">
      {/* Header */}
      <div className="jobs-header">
        <div className="header-content">
          <h1>💼 Quản lý Tuyển dụng</h1>
          <p>Quản lý các tin tuyển dụng và ứng viên</p>
        </div>
        <div className="header-actions">
          <button 
            className="create-btn"
            onClick={() => navigate('/admin/jobs/create')}
          >
            ➕ Tạo tin tuyển dụng
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📋</div>
          <div className="stat-content">
            <div className="stat-number">{stats.total}</div>
            <div className="stat-label">Tổng tin tuyển dụng</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🔥</div>
          <div className="stat-content">
            <div className="stat-number">{stats.active}</div>
            <div className="stat-label">Đang tuyển</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-number">{stats.filled}</div>
            <div className="stat-label">Đã tuyển đủ</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">👁️</div>
          <div className="stat-content">
            <div className="stat-number">{stats.views}</div>
            <div className="stat-label">Lượt xem</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-group">
            <input
              type="text"
              placeholder="🔍 Tìm kiếm theo tiêu đề, mô tả..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="filter-group">
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="filter-select"
            >
              <option value="">Tất cả trạng thái</option>
              {statuses.map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <select
              value={filters.department}
              onChange={(e) => handleFilterChange('department', e.target.value)}
              className="filter-select"
            >
              <option value="">Tất cả phòng ban</option>
              {departments.map(dept => (
                <option key={dept.value} value={dept.value}>
                  {dept.label}
                </option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <select
              value={filters.job_type}
              onChange={(e) => handleFilterChange('job_type', e.target.value)}
              className="filter-select"
            >
              <option value="">Tất cả loại</option>
              {jobTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedJobs.length > 0 && (
        <div className="bulk-actions">
          <div className="selected-count">
            Đã chọn {selectedJobs.length} tin tuyển dụng
          </div>
          <div className="bulk-controls">
            <select
              value={bulkAction}
              onChange={(e) => setBulkAction(e.target.value)}
              className="bulk-select"
            >
              <option value="">Chọn hành động...</option>
              <option value="active">Chuyển thành Đang tuyển</option>
              <option value="paused">Chuyển thành Tạm dừng</option>
              <option value="filled">Chuyển thành Đã tuyển đủ</option>
              <option value="expired">Chuyển thành Hết hạn</option>
              <option value="delete">Xóa</option>
            </select>
            <button
              onClick={handleBulkAction}
              disabled={!bulkAction}
              className="bulk-apply-btn"
            >
              Áp dụng
            </button>
          </div>
        </div>
      )}

      {/* Jobs Table */}
      <div className="jobs-table-container">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner">🔄</div>
            <p>Đang tải danh sách tin tuyển dụng...</p>
          </div>
        ) : jobs.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">💼</div>
            <h3>Chưa có tin tuyển dụng nào</h3>
            <p>Hãy tạo tin tuyển dụng đầu tiên để bắt đầu!</p>
            <button 
              onClick={() => navigate('/admin/jobs/create')}
              className="create-btn"
            >
              ➕ Tạo tin tuyển dụng
            </button>
          </div>
        ) : (
          <table className="jobs-table">
            <thead>
              <tr>
                <th width="50">
                  <input
                    type="checkbox"
                    checked={selectedJobs.length === jobs.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
                <th width="80">Hình ảnh</th>
                <th width="280">Vị trí tuyển dụng</th>
                <th width="120">Phòng ban</th>
                <th width="120">Loại</th>
                <th width="150">Mức lương</th>
                <th width="120">Địa điểm</th>
                <th width="100">Trạng thái</th>
                <th width="80">Lượt xem</th>
                <th width="150">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => {
                const statusBadge = getStatusBadge(job.status);
                
                return (
                  <tr key={job.id} className={selectedJobs.includes(job.id) ? 'selected' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedJobs.includes(job.id)}
                        onChange={(e) => handleSelectJob(job.id, e.target.checked)}
                      />
                    </td>
                    <td>
                      <div className="job-image-cell">
                        {job.image ? (
                          <img 
                            src={`${job.image}`} 
                            alt={job.title}
                            className="job-thumbnail"
                          />
                        ) : (
                          <div className="job-no-image">
                            📷
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="job-title-cell">
                        <div className="job-title">{job.title}</div>
                        {job.summary && (
                          <div className="job-summary">{job.summary}</div>
                        )}
                      </div>
                    </td>
                    <td>{getDepartmentLabel(job.department)}</td>
                    <td>{getJobTypeLabel(job.job_type)}</td>
                    <td>{formatSalary(job.min_salary, job.max_salary)}</td>
                    <td>
                      {job.location}
                      {job.remote_allowed && <div>🏠 Remote OK</div>}
                    </td>
                    <td>
                      <span className={`status-badge ${statusBadge.class}`}>
                        {statusBadge.label}
                      </span>
                    </td>
                    <td>{job.views}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => navigate(`/admin/jobs/edit/${job.id}`)}
                          className="edit-btn"
                          title="Chỉnh sửa"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleToggleStatus(job)}
                          className={`toggle-btn ${job.status === 'paused' ? 'paused' : ''}`}
                          title={job.status === 'active' ? 'Tạm dừng' : 'Kích hoạt'}
                        >
                          {job.status === 'active' ? '⏸️' : '▶️'}
                        </button>
                        <button
                          onClick={() => handleDeleteJob(job)}
                          className="delete-btn"
                          title="Xóa"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {!loading && jobs.length > 0 && (
        <div className="pagination">
          <button
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page <= 1}
            className="pagination-btn"
          >
            ← Trước
          </button>
          
          <span className="pagination-info">
            Trang {pagination.page} / {pagination.totalPages} 
            ({pagination.total} tin tuyển dụng)
          </span>
          
          <button
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page >= pagination.totalPages}
            className="pagination-btn"
          >
            Sau →
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminJobs; 
